import { useEffect, useRef, useState } from "react";
import GlobalContainer from "~/components/global_container";
import { captureEvent } from "~/helpers/capture_posthog_event";
import { apiFetch } from "~/lib/fetchClient";
import {
  isUnsupportedVariablesError,
  normalizeUnsupportedVariablesError,
} from "~/lib/unsupportedVariables";

const useTemplateManager = ({
  apiPath,
  apiResourceName,
  templateType,
  displayNameSingular,
  displayNamePlural,
  formState,
  isDifferent,
  onTemplateCreated,
  handleUnsupportedVariables = false,
}) => {
  const analyticsModule = GlobalContainer.product();
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [templateChanged, setTemplateChanged] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isCopying, setIsCopying] = useState(false);
  const [isSharing, setIsSharing] = useState(false);
  const [variablesWarning, setVariablesWarning] = useState(null);
  const [variablesWarningRetry, setVariablesWarningRetry] = useState(null);
  const isMountedRef = useRef(true);
  const requestRef = useRef(null);

  const loadTemplates = async () => {
    setIsLoading(true);
    try {
      const req = apiFetch.get(apiPath);
      requestRef.current = req;
      const loadedTemplates = await req;
      if (isMountedRef.current) {
        setTemplates(loadedTemplates);
      }
    } catch (error) {
      GlobalContainer.notify(
        GlobalContainer.renderErrors(error) ||
          `Failed to load ${displayNamePlural}`,
        "error",
      );
    } finally {
      if (isMountedRef.current) {
        setIsLoading(false);
      }
      requestRef.current = null;
    }
  };

  const createTemplate = async (
    payloadBuilder,
    { saveAnyway = false } = {},
  ) => {
    const payload = payloadBuilder();
    if (!payload) {
      return;
    }

    setIsCreating(true);
    try {
      const newTemplate = await apiFetch.post(apiPath, {
        json: { [apiResourceName]: payload, save_anyway: saveAnyway },
      });

      if (isMountedRef.current) {
        let newTemplates;
        setTemplates((prev) => {
          newTemplates = [...prev, newTemplate].sort((a, b) =>
            a.title.localeCompare(b.title),
          );
          return newTemplates;
        });
        const newOption = { label: newTemplate.title, value: newTemplate.id };
        setSelectedTemplate(newOption);
        onTemplateCreated(newTemplate, newTemplates);
      }

      // PostHog analytics
      captureEvent("template_created", {
        template_type: templateType,
        is_shared: !!payload.shared,
        module: analyticsModule,
      });

      GlobalContainer.notify("Template saved successfully.", "success");
    } catch (error) {
      if (handleUnsupportedVariables && isUnsupportedVariablesError(error)) {
        setVariablesWarning(normalizeUnsupportedVariablesError(error));
        setVariablesWarningRetry(
          () => () => createTemplate(payloadBuilder, { saveAnyway: true }),
        );
      } else {
        GlobalContainer.notify(
          GlobalContainer.renderErrors(error) ||
            "Failed to create template. Please try again.",
          "error",
        );
      }
    } finally {
      if (isMountedRef.current) {
        setIsCreating(false);
      }
    }
  };

  const updateTemplate = async (
    payloadBuilder,
    { saveAnyway = false } = {},
  ) => {
    const payload = payloadBuilder();
    if (!payload) {
      return;
    }

    setIsUpdating(true);
    try {
      const updatedTemplate = await apiFetch.put(`${apiPath}/${payload.id}`, {
        json: { [apiResourceName]: payload, save_anyway: saveAnyway },
      });

      if (isMountedRef.current) {
        setTemplates((prev) =>
          prev
            .map((t) => (t.id === updatedTemplate.id ? updatedTemplate : t))
            .sort((a, b) => a.title.localeCompare(b.title)),
        );
        setSelectedTemplate({
          label: updatedTemplate.title,
          value: updatedTemplate.id,
        });
      }
      GlobalContainer.notify("Template updated successfully.", "success");
    } catch (error) {
      if (handleUnsupportedVariables && isUnsupportedVariablesError(error)) {
        setVariablesWarning(normalizeUnsupportedVariablesError(error));
        setVariablesWarningRetry(
          () => () => updateTemplate(payloadBuilder, { saveAnyway: true }),
        );
      } else {
        GlobalContainer.notify(
          GlobalContainer.renderErrors(error) ||
            "Failed to update template. Please try again.",
          "error",
        );
      }
    } finally {
      if (isMountedRef.current) {
        setIsUpdating(false);
      }
    }
  };

  const deleteTemplate = async (templateID) => {
    setIsDeleting(true);
    try {
      await apiFetch.delete(`${apiPath}/${templateID}`);
      if (isMountedRef.current) {
        setTemplates((prev) => prev.filter((t) => t.id !== templateID));
        setSelectedTemplate(null);
      }
      GlobalContainer.notify("Template deleted.", "success");
    } catch (error) {
      GlobalContainer.notify(
        GlobalContainer.renderErrors(error) ||
          `Failed to delete ${displayNameSingular}`,
        "error",
      );
    } finally {
      if (isMountedRef.current) {
        setIsDeleting(false);
      }
    }
  };

  const copyTemplate = async (templateID) => {
    setIsCopying(true);
    try {
      const newTemplate = await apiFetch.post(
        `${apiPath}/${templateID}/copy`,
        {},
      );

      if (isMountedRef.current) {
        setTemplates((prev) =>
          [...prev, newTemplate].sort((a, b) => a.title.localeCompare(b.title)),
        );
        setSelectedTemplate({
          label: newTemplate.title,
          value: newTemplate.id,
        });
      }

      GlobalContainer.notify(
        "Template copied to your personal templates.",
        "success",
      );
    } catch (error) {
      GlobalContainer.notify(
        GlobalContainer.renderErrors(error) ||
          "Failed to copy template. Please try again.",
        "error",
      );
    } finally {
      if (isMountedRef.current) {
        setIsCopying(false);
      }
    }
  };

  const shareTemplate = async (templateID) => {
    setIsSharing(true);
    try {
      const updatedTemplate = await apiFetch.put(`${apiPath}/${templateID}`, {
        json: { [apiResourceName]: { shared: true } },
      });

      if (isMountedRef.current) {
        setTemplates((prev) =>
          prev.map((t) => (t.id === updatedTemplate.id ? updatedTemplate : t)),
        );
      }

      GlobalContainer.notify("Template shared with your account.", "success");
    } catch (error) {
      GlobalContainer.notify(
        GlobalContainer.renderErrors(error) ||
          "Failed to update sharing settings. Please try again.",
        "error",
      );
    } finally {
      if (isMountedRef.current) {
        setIsSharing(false);
      }
    }
  };

  const unshareTemplate = async (templateID) => {
    setIsSharing(true);
    try {
      const updatedTemplate = await apiFetch.put(`${apiPath}/${templateID}`, {
        json: { [apiResourceName]: { shared: false } },
      });

      if (isMountedRef.current) {
        setTemplates((prev) =>
          prev.map((t) => (t.id === updatedTemplate.id ? updatedTemplate : t)),
        );
      }

      GlobalContainer.notify("Template is now private.", "success");
    } catch (error) {
      GlobalContainer.notify(
        GlobalContainer.renderErrors(error) ||
          "Failed to update sharing settings. Please try again.",
        "error",
      );
    } finally {
      if (isMountedRef.current) {
        setIsSharing(false);
      }
    }
  };

  const applyTemplate = (template) => {
    if (!template) {
      return;
    }
    captureEvent("template_applied", {
      template_type: templateType,
      is_shared: !!template.shared,
      module: analyticsModule,
    });
  };

  useEffect(() => {
    loadTemplates();
    return () => {
      isMountedRef.current = false;
      requestRef.current?.abort?.();
      requestRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (selectedTemplate) {
      const template = templates.find((t) => t.id === selectedTemplate.value);
      if (template) {
        setTemplateChanged(isDifferent(formState, template));
      }
    } else {
      setTemplateChanged(false);
    }
  }, [formState, selectedTemplate, templates, isDifferent]);

  return {
    templates,
    selectedTemplate,
    setSelectedTemplate,
    templateChanged,
    setTemplateChanged,
    isLoading,
    isCreating,
    isUpdating,
    isDeleting,
    isCopying,
    isSharing,
    variablesWarning,
    setVariablesWarning,
    variablesWarningRetry,
    createTemplate,
    updateTemplate,
    deleteTemplate,
    copyTemplate,
    shareTemplate,
    unshareTemplate,
    applyTemplate,
  };
};

export default useTemplateManager;
